import { useState } from 'react';
import { Activity as ActivityIcon, Filter } from 'lucide-react';
import { ActivityCard } from './ActivityCard';
import { ActivityFilters } from './ActivityFilters';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import type { Activity, ActivityFilters as ActivityFiltersType } from '@/types/activity';

interface ActivityListProps {
  activities: Activity[];
  isLoading: boolean;
  filters: ActivityFiltersType;
  onFiltersChange: (filters: Partial<ActivityFiltersType>) => void;
  onFiltersClear: () => void;
  onActivityClick?: (activity: Activity) => void;
  showFilters?: boolean;
}

export function ActivityList({
  activities,
  isLoading,
  filters,
  onFiltersChange,
  onFiltersClear,
  onActivityClick,
  showFilters = true,
}: ActivityListProps) {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        {showFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            leftIcon={<Filter className="h-4 w-4" />}
          >
            Filters
          </Button>
        )}
      </div>

      {showFilters && isFiltersOpen && (
        <ActivityFilters
          filters={filters}
          onChange={onFiltersChange}
          onClear={onFiltersClear}
        />
      )}

      {activities.length === 0 ? (
        <EmptyState
          icon={<ActivityIcon className="h-12 w-12" />}
          title="No activities found"
          description="No activities match your current filters"
          action={
            Object.keys(filters).length > 0 ? (
              <Button variant="outline" onClick={onFiltersClear}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-3">
          {activities.map((activity) => (
            <ActivityCard
              key={activity.id}
              activity={activity}
              onClick={onActivityClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}
